import { createSelector } from 'reselect';

export const selectMedium = (state, id, type) => {
  if (type) return state.entities[type] && state.entities[type][id];

  const media = ['texts', 'images', 'audios', 'videos'].reduce((acc, el) => {
    if (!state.entities[el]) return acc;

    return {
      ...acc,
      ...state.entities[el],
    };
  }, {});
  return media[id];
};

export const selectVideo = (state, id) => state.entities.videos[id];

export const selectIsUploadUnderway = state =>
  !!state.storyMedia.uploadBatch.filter(item => item.status === 'uploading').length;

export const selectUploadProgress = (state, mediumId) => {
  const progressItem = state.storyMedia.uploadBatch.find(item => item.id === mediumId);
  return progressItem.uploadProgress;
};

// What went wrong with this medium, if anything.
//
// The distinction between an upload failure and a processing failure matters,
// because the file is still safe in one case and not in the other -- and this
// used to collapse them. `medium.state === 'error'` with a message returned
// 'upload failed', which rendered the upload prompt telling someone their upload
// had failed and offering to delete it, when in fact the upload had succeeded and
// only derivative generation had not.
//
// That is not a hypothetical: some HEIC files fail in ImageMagick with
// `Too many auxiliary image references @ error/heic.c` -- newer iPhones store HDR
// gain maps as auxiliary images -- so the original is in S3 and downloadable while
// the medium sits in `error`. The person was told to delete a file that was fine.
export const selectMediumError = createSelector(
  [selectMedium, (state, id) => state.storyMedia.uploadBatch.find(item => item.id === id)],
  (medium, progressItem) => {
    // A finished medium is finished. `error-message-cache` is a cache of the last
    // ErrorLog message and is not cleared when the medium recovers, so checking it
    // without looking at state showed an error prompt over media that had
    // completed successfully.
    if (medium?.state === 'finished') return '';

    // The bytes never arrived. Nothing to salvage server side.
    if (medium?.state === 'stalled_upload') return 'upload stalled';

    // This browser's attempt to PUT the file failed.
    if (progressItem && progressItem.status === 'error') return 'upload failed';

    // The upload arrived; making something usable out of it did not. The original
    // is in S3, so this must not be reported as an upload problem.
    if (medium?.state === 'error' && medium['error-message-cache']) return 'processing failed';

    // Mid-flight, carrying an error from an earlier attempt.
    if (medium?.['error-message-cache']) return 'processing';

    return '';
  }
);

export const selectRejectedMedia = state => state.storyMedia.rejectedMedia;

export default selectMedium;
